import { api } from "@/lib/api";

/**
 * The reporting client.
 *
 * ⚠️ Everything here is an AGGREGATE over requests the caller can already read. No shape in
 * this file carries a subject, a description, a comment or a person's contact details, and
 * none of them should ever grow one — the server computes these under the same query filter
 * and row-level security as the queue, and the value of that is lost the moment a response
 * starts carrying rows instead of counts.
 */

/** The ranges the server accepts. Mirrors `AnalyticsEndpoints.AllowedRangeDays`. */
export const ANALYTICS_RANGES = [1, 3, 7, 14, 30, 60, 90] as const;

export type AnalyticsRangeDays = (typeof ANALYTICS_RANGES)[number];

export const DEFAULT_ANALYTICS_RANGE: AnalyticsRangeDays = 30;

/**
 * A period written out, for prose that reads "over the last …".
 *
 * ⚠️ One day is a real option, so the plural cannot be hard-coded. "the last 1 days" above a
 * figure is the kind of seam that makes a reader doubt the number underneath it.
 */
export function rangePhrase(days: number): string {
  return days === 1 ? "1 day" : `${days} days`;
}

/**
 * What the figures on the page cover.
 *
 * ⚠️ Rendered, never branched on for access — the same rule the permission stamp follows.
 * Two administrators legitimately see different totals here, because row-level security
 * scopes an aggregate exactly as it scopes a page of the queue, and the screen's job is to
 * say so rather than to imply everyone is looking at the same organisation.
 */
export interface AnalyticsScope {
  isOrganisationWide: boolean;
  requestsInRange: number;
  teamNames: string[];
  clearedLevels: string[];
  excludedLevels: string[];
  timeZoneId: string;
}

export interface AnalyticsRange {
  days: number;
  startUtc: string;
  endUtc: string;
  previousStartUtc: string;
  timeZoneId: string;
}

/**
 * The headline figures.
 *
 * Each windowed measure carries its own previous-period value rather than a percentage, so
 * the screen decides how to present a change — and can decline to present one at all when
 * the previous period had nothing to divide by.
 *
 * ⚠️ `openNow`, `unassignedNow` and `breachingNow` are POINT-IN-TIME and ignore the range.
 * "Open over the last 90 days" is not a quantity anybody means, and a tile that silently
 * changed meaning with the range selector would be the most quotable wrong number here.
 */
export interface AnalyticsHeadline {
  created: number;
  createdPrevious: number;
  resolved: number;
  resolvedPrevious: number;
  backlogDelta: number;
  backlogDeltaPrevious: number;
  averageFirstResponseMinutes: number | null;
  averageFirstResponseMinutesPrevious: number | null;
  averageResolutionMinutes: number | null;
  averageResolutionMinutesPrevious: number | null;
  openNow: number;
  unassignedNow: number;
  breachingNow: number;
}

export interface AnalyticsVolumePoint {
  day: string;
  created: number;
  resolved: number;
}

export interface AnalyticsOverview {
  scope: AnalyticsScope;
  range: AnalyticsRange;
  headline: AnalyticsHeadline;
  volume: AnalyticsVolumePoint[];
  computedAtUtc: string;
}

/**
 * A rate, as its two parts.
 *
 * ⚠️ The denominator is always present and the screen always prints it. "94%" over eleven
 * requests and "94%" over nine hundred are the same number and different facts. `percent` is
 * null rather than zero when nothing qualified — no data is not the same as no compliance.
 */
export interface AnalyticsRatio {
  numerator: number;
  denominator: number;
  percent: number | null;
}

export interface AnalyticsQualityRates {
  cohort: number;
  firstResponseSla: AnalyticsRatio;
  resolutionSla: AnalyticsRatio;
  reopened: AnalyticsRatio;
  firstContactResolution: AnalyticsRatio;
  escalated: AnalyticsRatio;
}

export interface AnalyticsCompliancePoint {
  day: string;
  firstResponse: AnalyticsRatio;
  resolution: AnalyticsRatio;
}

export interface AnalyticsQuality {
  scope: AnalyticsScope;
  range: AnalyticsRange;
  current: AnalyticsQualityRates;
  previous: AnalyticsQualityRates;
  compliance: AnalyticsCompliancePoint[];
  computedAtUtc: string;
}

/** ⚠️ `open` excludes `breaching`, so the two stack without double-counting the bar. */
export interface AnalyticsAgingBand {
  label: string;
  open: number;
  breaching: number;
}

export interface AnalyticsSlaRisk {
  priority: string;
  breaching: number;
  atRisk: number;
  paused: number;
  onTrack: number;
  noTarget: number;
}

export interface AnalyticsDurationBand {
  label: string;
  count: number;
}

export interface AnalyticsBacklog {
  scope: AnalyticsScope;
  range: AnalyticsRange;
  aging: AnalyticsAgingBand[];
  slaRisk: AnalyticsSlaRisk[];
  resolutionTimes: AnalyticsDurationBand[];
  computedAtUtc: string;
}

export interface AnalyticsBreakdownRow {
  key: string | null;
  label: string;
  raised: number;
  /** The same count over the equal-length period before, so a row can say which way it moved. */
  raisedPrevious: number;
  finished: number;
  openNow: number;
  breachingNow: number;
  resolutionSla: AnalyticsRatio;
  averageResolutionMinutes: number | null;
  note: string | null;
}

/**
 * ⚠️ `total` is the sum of `raised` across every row, and it equals the headline. The screen
 * states that rather than leaving somebody to add up a column and wonder why it disagrees
 * with the tile above it — which is how a reporting screen loses its credibility in one
 * meeting.
 */
export interface AnalyticsBreakdownGroup {
  rows: AnalyticsBreakdownRow[];
  total: number;
  totalOpenNow: number;
}

export interface AnalyticsBreakdown {
  scope: AnalyticsScope;
  range: AnalyticsRange;
  bySite: AnalyticsBreakdownGroup;
  byCategory: AnalyticsBreakdownGroup;
  byTeam: AnalyticsBreakdownGroup;
  computedAtUtc: string;
}

/**
 * One agent's work over the period.
 *
 * ⚠️ Carries a name, a job title and whether a picture exists — no email, no UPN, no
 * `(TenantId, ObjectId)`, no employee id, no phone. The same line D-38 and D-51 draw for every
 * other people-bearing payload in the product.
 */
export interface AnalyticsAgentRow {
  userId: string;
  name: string;
  jobTitle: string | null;
  hasAvatar: boolean;
  resolved: number;
  openNow: number;
  breachingNow: number;
  resolutionSla: AnalyticsRatio;
  fixedFirstTime: AnalyticsRatio;
  reopened: AnalyticsRatio;
  averageResolutionMinutes: number | null;
  averageFirstResponseMinutes: number | null;
  /** False when too few requests were resolved for a rate to mean anything. */
  ratesReportable: boolean;

  /**
   * Recorded working time inside the period.
   *
   * ⚠️ Only what somebody actually started a timer for. It is a floor on effort, never a
   * measure of it — an agent who never presses start reads as zero, so a low figure is
   * evidence about the timer's adoption before it is evidence about a person.
   */
  handleMinutes: number;
  averageHandleMinutes: number | null;

  /** Sessions nobody stopped, closed by the 8-hour cap. Shown, never hidden. */
  autoClosedSessions: number;

  /**
   * Average stars from requesters, or null below the reporting floor.
   *
   * ⚠️ On this card only because reputation stopped being unscoped (D-64). D-55 withheld it
   * while the reputation cards averaged every rating in the system and this table was
   * RLS-scoped; both now come from the same readable set.
   *
   * ⚠️ NOT windowed to the selected period, unlike every other figure in this row. A rating
   * arrives days after the work — the prompt alone waits 24 hours — so windowing it would
   * systematically drop feedback for requests resolved near the end of the range.
   */
  averageStars: number | null;

  /**
   * How many ratings that average is over. Rides with it always, and is the only way to tell
   * "nobody rated" from "too few to publish" — both render as a dash.
   */
  ratingCount: number;
}

export interface AnalyticsAgents {
  scope: AnalyticsScope;
  range: AnalyticsRange;
  agents: AnalyticsAgentRow[];
  /** Whether the reader may open a staff record — `user.manage`, exactly as on a ticket. */
  canViewPerson: boolean;
  /** Across everyone in scope, so the card can qualify the whole column at once. */
  autoClosedSessions: number;
  computedAtUtc: string;
}

export interface AnalyticsQuery {
  days: AnalyticsRangeDays;
  siteId?: string;
  teamId?: string;
}

function queryString(query: AnalyticsQuery): string {
  const params = new URLSearchParams({ days: String(query.days) });
  if (query.siteId) params.set("siteId", query.siteId);
  if (query.teamId) params.set("teamId", query.teamId);
  return params.toString();
}

export function fetchAnalyticsOverview(
  query: AnalyticsQuery,
  signal?: AbortSignal,
): Promise<AnalyticsOverview> {
  return api<AnalyticsOverview>(`/api/analytics/overview?${queryString(query)}`, { signal });
}

export function fetchAnalyticsQuality(
  query: AnalyticsQuery,
  signal?: AbortSignal,
): Promise<AnalyticsQuality> {
  return api<AnalyticsQuality>(`/api/analytics/quality?${queryString(query)}`, { signal });
}

export function fetchAnalyticsBacklog(
  query: AnalyticsQuery,
  signal?: AbortSignal,
): Promise<AnalyticsBacklog> {
  return api<AnalyticsBacklog>(`/api/analytics/backlog?${queryString(query)}`, { signal });
}

export function fetchAnalyticsBreakdown(
  query: AnalyticsQuery,
  signal?: AbortSignal,
): Promise<AnalyticsBreakdown> {
  return api<AnalyticsBreakdown>(`/api/analytics/breakdown?${queryString(query)}`, { signal });
}

export function fetchAnalyticsAgents(
  query: AnalyticsQuery,
  signal?: AbortSignal,
): Promise<AnalyticsAgents> {
  return api<AnalyticsAgents>(`/api/analytics/agents?${queryString(query)}`, { signal });
}

// ---------------------------------------------------------------------------
// Formatting. Kept here rather than in the components so the same number reads
// identically on a tile, in a tooltip and in a table.
// ---------------------------------------------------------------------------

/**
 * A duration in minutes, rendered at the resolution a person would actually say it.
 *
 * ⚠️ Null is "nothing to report", never zero. An average over an empty set is undefined,
 * and rendering it as `0m` would read as a service desk answering instantly — which is
 * exactly what it looks like when nobody has answered anything at all.
 */
export function formatDuration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return "—";
  if (minutes < 1) return "<1m";
  if (minutes < 90) return `${Math.round(minutes)}m`;

  const hours = minutes / 60;
  if (hours < 48) return `${hours.toFixed(1)}h`;

  return `${(hours / 24).toFixed(1)}d`;
}

export function formatCount(value: number): string {
  return value.toLocaleString();
}

/** A signed count, so a backlog that grew reads as `+12` rather than `12`. */
export function formatSigned(value: number): string {
  return value > 0 ? `+${formatCount(value)}` : formatCount(value);
}

export function formatPercent(ratio: AnalyticsRatio | null | undefined): string {
  if (!ratio || ratio.percent === null) return "—";
  return `${ratio.percent.toFixed(ratio.percent < 10 ? 1 : 0)}%`;
}

/**
 * "of 143" — the sample a rate rests on.
 *
 * ⚠️ Never omitted, and never abbreviated away on a narrow screen. A rate whose denominator
 * is hidden is the shape of every reporting mistake this screen is trying not to make.
 */
export function formatSample(ratio: AnalyticsRatio | null | undefined): string {
  if (!ratio || ratio.denominator === 0) return "nothing determined yet";
  return `of ${formatCount(ratio.denominator)}`;
}

/**
 * "13 Aug" from an ISO calendar date.
 *
 * ⚠️ Split rather than parsed. `new Date("2026-08-13")` is midnight UTC, which renders as the
 * 12th for every site west of Greenwich — the exact off-by-one the server bucketed in the
 * reader's own timezone to avoid, reintroduced at the last step.
 */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatDayLabel(isoDate: string): string {
  const [, month, day] = isoDate.split("-").map(Number);
  return `${day} ${MONTHS[(month ?? 1) - 1]}`;
}

/**
 * Below this, a movement is noise.
 *
 * ⚠️ One request becoming three is "+200%", and it is the most alarming number on any
 * dashboard that does not have a floor. Movers are computed over the larger of the two
 * periods, so a category that collapsed from forty to two still qualifies — the guard is
 * against small NUMBERS, not against falls.
 */
export const MOVER_FLOOR = 5;

export interface Mover {
  key: string | null;
  label: string;
  raised: number;
  previous: number;
  delta: number;
  /** Null when the previous period had nothing to divide by. */
  percent: number | null;
}

/**
 * The categories, sites or teams that moved most against the period before.
 *
 * ⚠️ Ranked by the SIZE of the change, not by the percentage. Percentage ranking puts every
 * tiny category at the top of the list every time, which is why the mock's trending panel
 * would have been dominated by whatever happened to have two tickets last week.
 */
export function moversOf(rows: AnalyticsBreakdownRow[], limit = 6): Mover[] {
  return rows
    .filter((row) => Math.max(row.raised, row.raisedPrevious) >= MOVER_FLOOR)
    .map((row) => ({
      key: row.key,
      label: row.label,
      raised: row.raised,
      previous: row.raisedPrevious,
      delta: row.raised - row.raisedPrevious,
      percent:
        row.raisedPrevious === 0
          ? null
          : ((row.raised - row.raisedPrevious) / row.raisedPrevious) * 100,
    }))
    .filter((mover) => mover.delta !== 0)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, limit);
}

export type TrendDirection = "up" | "down" | "flat" | "unknown";

export interface Trend {
  direction: TrendDirection;
  /** Percentage change, or null when the previous period had nothing to divide by. */
  percent: number | null;
  label: string;
}

/**
 * Change against the equal-length period immediately before.
 *
 * ⚠️ Returns `unknown` rather than `+100%` when the previous period was zero or absent.
 * Dividing by nothing produces the most confident-looking number on the page and the least
 * meaningful one — "up 100%" on a desk that went from zero tickets to one is a sentence
 * somebody will repeat in a meeting.
 */
export function trendOf(current: number | null, previous: number | null): Trend {
  if (current === null || previous === null) {
    return { direction: "unknown", percent: null, label: "no comparison" };
  }

  if (previous === 0) {
    return current === 0
      ? { direction: "flat", percent: 0, label: "no change" }
      : { direction: "unknown", percent: null, label: "no prior period" };
  }

  const percent = ((current - previous) / Math.abs(previous)) * 100;

  if (Math.abs(percent) < 0.5) return { direction: "flat", percent: 0, label: "no change" };

  return {
    direction: percent > 0 ? "up" : "down",
    percent,
    label: `${percent > 0 ? "+" : ""}${percent.toFixed(0)}%`,
  };
}

/**
 * How a RATE moved, in percentage points.
 *
 * ⚠️ Points, not percent. SLA compliance going from 90% to 94% is **+4pp**, and reporting it
 * as "+4.4%" is a different claim about a different quantity — one that sounds smaller than
 * the improvement actually was, and that compounds wrongly if anybody chains two of them.
 * The unit is on the label so nobody has to guess which was meant.
 *
 * ⚠️ Undetermined on either side yields no comparison at all. A period where nothing was
 * determined has no rate to compare against, and treating its absent value as zero would
 * report a collapse the moment a quiet week ends.
 */
export function pointChangeOf(
  current: AnalyticsRatio | null | undefined,
  previous: AnalyticsRatio | null | undefined,
): Trend {
  if (
    !current ||
    !previous ||
    current.percent === null ||
    previous.percent === null ||
    previous.denominator === 0
  ) {
    return { direction: "unknown", percent: null, label: "no comparison" };
  }

  const points = current.percent - previous.percent;

  if (Math.abs(points) < 0.5) return { direction: "flat", percent: 0, label: "no change" };

  return {
    direction: points > 0 ? "up" : "down",
    percent: points,
    label: `${points > 0 ? "+" : ""}${points.toFixed(1)}pp`,
  };
}
